import { Room, RoomEvent, Track } from "livekit-client";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { shouldSubscribeSpatiusBaseRoomPublication } from "./spatius-primary-session-policy";

type MutableRef<T> = {
  current: T;
};

type BaseRoomEffectsLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
};

type UseLiveKitBaseRoomEffectsInput = {
  currentRoom: Room;
  baseRoom: Room;
  isConnected: boolean;
  isRecording: boolean;
  selectedLanguageCode: string;
  agentState: string;
  shouldUseSpatiusPrimaryRoom: boolean;
  audioPlaybackBlockedMessage: string;
  latencyAgentStateMarksRef: MutableRef<Set<string>>;
  firstRemoteAudioMarkedRef: MutableRef<boolean>;
  clearSpatiusPrimaryBaseAudioWatchdogTimer: () => void;
  ensureSpatiusPrimaryBaseRoomAudioPlayback: (
    targetRoom: Room,
    reason: string,
    options?: { startPlayback?: boolean }
  ) => Promise<{ avatarAudioPublicationCount: number }>;
  logDuplicateAudioPublications: (reason: string, targetRoom?: Room) => unknown;
  syncSpatiusPrimaryBaseRoomAudioSubscriptions: (targetRoom: Room) => void;
  markLatencyPhase: (phase: string, data?: Record<string, unknown>) => void;
  setAudioTrackError: Dispatch<SetStateAction<string | null>>;
  logger: BaseRoomEffectsLogger;
};

export function useLiveKitBaseRoomEffects({
  currentRoom,
  baseRoom,
  isConnected,
  isRecording,
  selectedLanguageCode,
  agentState,
  shouldUseSpatiusPrimaryRoom,
  audioPlaybackBlockedMessage,
  latencyAgentStateMarksRef,
  firstRemoteAudioMarkedRef,
  clearSpatiusPrimaryBaseAudioWatchdogTimer,
  ensureSpatiusPrimaryBaseRoomAudioPlayback,
  logDuplicateAudioPublications,
  syncSpatiusPrimaryBaseRoomAudioSubscriptions,
  markLatencyPhase,
  setAudioTrackError,
  logger,
}: UseLiveKitBaseRoomEffectsInput) {
  useEffect(() => {
    if (isConnected) {
      const languagePreference = {
        type: "language_preference",
        languageCode: selectedLanguageCode,
      };
      currentRoom.localParticipant.publishData(Buffer.from(JSON.stringify(languagePreference)));
    }
  }, [currentRoom, isConnected, selectedLanguageCode]);

  useEffect(() => {
    if (!isRecording) {
      return;
    }

    const stateKey = String(agentState || "unknown").toLowerCase();
    if (latencyAgentStateMarksRef.current.has(stateKey)) {
      return;
    }

    latencyAgentStateMarksRef.current.add(stateKey);
    markLatencyPhase(`agent_state_${stateKey}`);
  }, [agentState, isRecording, latencyAgentStateMarksRef, markLatencyPhase]);

  useEffect(() => {
    const onTrackSubscribed = (track: any, publication: any, participant: any) => {
      const isSpatiusAvatarAudio = shouldSubscribeSpatiusBaseRoomPublication({
        participantIdentity: participant?.identity,
        publicationKind: publication?.kind ?? track?.kind,
      });

      if (isSpatiusAvatarAudio) {
        clearSpatiusPrimaryBaseAudioWatchdogTimer();
        logger.info("Spatius avatar audio subscribed in base room", {
          participantIdentity: participant?.identity || null,
          trackSid: publication?.trackSid || publication?.sid || null,
          muted: Boolean(publication?.isMuted ?? publication?.muted),
        });
        void ensureSpatiusPrimaryBaseRoomAudioPlayback(currentRoom, "track_subscribed", {
          startPlayback: true,
        });
      }

      logDuplicateAudioPublications("track_subscribed", currentRoom);

      if (firstRemoteAudioMarkedRef.current) {
        return;
      }

      if (track?.kind !== Track.Kind.Audio) {
        return;
      }

      if (participant?.identity === currentRoom.localParticipant.identity) {
        return;
      }

      firstRemoteAudioMarkedRef.current = true;
      markLatencyPhase("first_remote_audio_track_subscribed", {
        participantIdentity: participant?.identity || null,
        trackSource: publication?.source || null,
      });
    };

    currentRoom.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    return () => {
      currentRoom.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
    };
  }, [
    clearSpatiusPrimaryBaseAudioWatchdogTimer,
    currentRoom,
    ensureSpatiusPrimaryBaseRoomAudioPlayback,
    firstRemoteAudioMarkedRef,
    logDuplicateAudioPublications,
    logger,
    markLatencyPhase,
  ]);

  useEffect(() => {
    if (!isRecording || !isConnected) {
      return;
    }

    logDuplicateAudioPublications("active_session_interval", currentRoom);
    const intervalId = window.setInterval(() => {
      logDuplicateAudioPublications("active_session_interval", currentRoom);
    }, 2000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentRoom, isConnected, isRecording, logDuplicateAudioPublications]);

  useEffect(() => {
    if (!shouldUseSpatiusPrimaryRoom) {
      return;
    }

    const syncSubscriptions = () => {
      syncSpatiusPrimaryBaseRoomAudioSubscriptions(baseRoom);
      void ensureSpatiusPrimaryBaseRoomAudioPlayback(baseRoom, "spatius_subscription_sync", {
        startPlayback: true,
      });
      logDuplicateAudioPublications("spatius_subscription_sync", baseRoom);
    };

    syncSubscriptions();
    baseRoom.on(RoomEvent.ParticipantConnected, syncSubscriptions);
    baseRoom.on(RoomEvent.TrackPublished, syncSubscriptions);
    baseRoom.on(RoomEvent.TrackSubscribed, syncSubscriptions);

    return () => {
      baseRoom.off(RoomEvent.ParticipantConnected, syncSubscriptions);
      baseRoom.off(RoomEvent.TrackPublished, syncSubscriptions);
      baseRoom.off(RoomEvent.TrackSubscribed, syncSubscriptions);
    };
  }, [
    baseRoom,
    ensureSpatiusPrimaryBaseRoomAudioPlayback,
    logDuplicateAudioPublications,
    shouldUseSpatiusPrimaryRoom,
    syncSpatiusPrimaryBaseRoomAudioSubscriptions,
  ]);

  useEffect(() => {
    const onAudioPlaybackStatusChanged = () => {
      if (currentRoom.canPlaybackAudio) {
        setAudioTrackError((prev) => (prev === audioPlaybackBlockedMessage ? null : prev));
        return;
      }

      logger.warn("Browser blocked LiveKit audio playback", {
        roomName: currentRoom.name || null,
      });
      setAudioTrackError(audioPlaybackBlockedMessage);
    };

    currentRoom.on(RoomEvent.AudioPlaybackStatusChanged, onAudioPlaybackStatusChanged);
    return () => {
      currentRoom.off(RoomEvent.AudioPlaybackStatusChanged, onAudioPlaybackStatusChanged);
    };
  }, [audioPlaybackBlockedMessage, currentRoom, logger, setAudioTrackError]);
}
